import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";

import { colors } from "utils";
import {
  BodyRow,
  Cell,
  HeaderButton,
  SortIcon,
  SortableHeader,
  Table,
  TableWrapper,
} from "./reportTableStyles";
import {
  INITIAL_VISIBLE,
  ROSTER_SORT_KEYS,
  VISIBLE_CHUNK,
  nextRosterSort,
  rosterDateText,
  rosterMinutesText,
  rosterNameText,
  studentNameHeader,
  visibleRows,
} from "./studentRoster";

// The Student Reports landing screen (node 4085:23172): every student the
// educator may open a report for, and the three figures they would pick one by.
//
// This is the SAME table the report's own tabs draw — the shared header, cell
// and row styles, at the 16px padding this frame uses rather than the 8px the
// report tables use. A second table that merely looked like it would drift the
// first time either was adjusted.

export const StudentSelectionTable = ({
  rows = [],
  resetKey = "",
  sort = null,
  onSortChange,
  onSelect,
}) => {
  const [count, setCount] = useState(INITIAL_VISIBLE);
  const sentinelRef = useRef(null);
  const visible = visibleRows(rows, count);
  const hasMore = count < rows.length;

  // Re-sorting or re-filtering starts the reader at the top of a different
  // list; keeping their scroll depth would show them the middle of an order
  // they never scrolled through.
  //
  // Keyed on WHAT CHANGED rather than on the rows array's identity. The parent
  // sorts into a fresh array, and `useMemo` is a hint React may discard, so
  // depending on identity would let an incidental re-render drop a reader who
  // had scrolled to row 150 back to row 50.
  useEffect(() => {
    setCount(INITIAL_VISIBLE);
  }, [resetKey]);

  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === "undefined") return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setCount((current) => current + VISIBLE_CHUNK);
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, count]);

  // Built here rather than at module scope: `utils` re-exports the route table,
  // which reaches this file again, so a top-level array reading
  // ROSTER_SORT_KEYS evaluates before that import has resolved.
  const columns = [
    { key: ROSTER_SORT_KEYS.NAME, label: studentNameHeader(rows.length) },
    { key: ROSTER_SORT_KEYS.SESSION_COUNT, label: "Session Count" },
    { key: ROSTER_SORT_KEYS.TOTAL_MINUTES, label: "Total Session Time" },
    { key: ROSTER_SORT_KEYS.LAST_SESSION, label: "Last Session Date" },
  ];

  const sortIcon = (column) => {
    if (sort?.column !== column) return faSort;
    return sort.direction === "asc" ? faSortUp : faSortDown;
  };

  return (
    <>
      <TableWrapper>
        <RosterTable>
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} width="25%" />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <RosterHeader scope="col" key={column.key}>
                  <RosterHeaderButton
                    type="button"
                    onClick={() => onSortChange(nextRosterSort(sort, column.key))}
                  >
                    <span>{column.label}</span>
                    <SortIcon
                      icon={sortIcon(column.key)}
                      $isActive={sort?.column === column.key}
                    />
                  </RosterHeaderButton>
                </RosterHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <BodyRow
                key={row.student_id}
                $clickable={false}
                data-testid="roster-row"
              >
                <RosterCell>
                  <StudentLink type="button" onClick={() => onSelect(row)}>
                    {rosterNameText(row)}
                  </StudentLink>
                </RosterCell>
                <RosterCell>{row.session_count ?? 0}</RosterCell>
                <RosterCell>{rosterMinutesText(row.total_minutes)}</RosterCell>
                <RosterCell>{rosterDateText(row.last_session_date)}</RosterCell>
              </BodyRow>
            ))}
          </tbody>
        </RosterTable>
        {/* The rest arrive when the reader reaches this, and it says so:
            a table that just stops at fifty of a hundred and fifty reads as
            the whole roster. */}
        {hasMore && (
          <Sentinel ref={sentinelRef} role="status" data-testid="roster-sentinel">
            {`Showing ${visible.length} of ${rows.length} students…`}
          </Sentinel>
        )}
      </TableWrapper>
    </>
  );
};

const RosterTable = styled(Table)`
  table-layout: fixed;
`;

// 16px of padding rather than the report tables' 8px, which is what makes the
// header 56px and each row 54px in the frame.
const RosterHeader = styled(SortableHeader)`
  padding: 0;
`;

const RosterHeaderButton = styled(HeaderButton)`
  justify-content: space-between;
  padding: 16px;
`;

const RosterCell = styled(Cell)`
  padding: 16px;
  background: ${colors.gray50};
`;

const StudentLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.blue700};
  text-decoration: underline;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${colors.blue800};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;



const Sentinel = styled.div`
  padding: 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.milkGlass950};
  text-align: center;
`;
